/* eslint-disable unicorn/filename-case -- TODO rename later */
import { clientSDK, queryClient } from "@istari-frontend/api-client";
import { Artifact, ControlTag, NewControlTag, UpdateControlTag, V3Models } from "@istari/istari-client";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { controlTagCatalogQueryOptions, controlTagQueryKeys } from "~/control-tags/data-source/use-control-tags";
import { resourceQueryKeys } from "~/resource/data-source/api/resource-query-options";

import { modelQueryKeys } from "./model/modelQueryKeys";
import { artifactQueryKeys } from "./useModels";

function invalidateControlTagCatalogs() {
  void queryClient.invalidateQueries({ queryKey: controlTagQueryKeys.catalog });
}

export type LabeledControlTag = Omit<ControlTag, "client" | "setClient"> & {
  color: string;
  label: string;
  value: string;
  status: string;
};

export function toLabeledControlTags(tags: ControlTag[] | undefined): LabeledControlTag[] {
  // Rest-spread instead of re-listing fields, so a new ControlTag field can't be silently dropped
  // out of sync with the Omit in LabeledControlTag.
  return (tags ?? []).map(({ client: _client, ...tag }) => ({
    ...tag,
    color: tag.displayColor ?? "",
    label: tag.name,
    value: tag.id,
  }));
}

function getLabeledControlTagsFromCache(): LabeledControlTag[] {
  return toLabeledControlTags(queryClient.getQueryData<ControlTag[]>(controlTagQueryKeys.catalog));
}

export const useAddControlTag = (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
  useMutation({
    mutationFn: async ({
      name,
      description,
      displayColor = "oklch(0.373 0.034 259.733)",
    }: {
      name: string;
      description?: string | null | undefined;
      displayColor?: string | null | undefined;
    }) => {
      await clientSDK
        .createControlTag({ name, description, displayColor } as NewControlTag)
        .then((res) => res)
        .catch((err: Error) => {
          console.error(err);
          throw new Error("An error occurred while adding a control tag.", { cause: err });
        });
    },
    onSuccess: () => {
      invalidateControlTagCatalogs();
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });

export const useEditControlTag = (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
  useMutation({
    mutationFn: async ({
      controlTagId,
      name,
      description,
      displayColor = "oklch(0.373 0.034 259.733)",
    }: {
      controlTagId: string;
      name: string;
      description?: string | null | undefined;
      displayColor?: string | null | undefined;
    }) => {
      const updateTag = new UpdateControlTag(controlTagId, name, description, null, displayColor);
      await clientSDK
        .updateControlTag(updateTag)
        .then((res) => res)
        .catch((err: Error) => {
          console.error(err);
          throw new Error("An error occurred while editing the control tag.", { cause: err });
        });
    },
    onSuccess: () => {
      invalidateControlTagCatalogs();
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });

export const useGetControlTags = (options?: { enabled?: boolean }) =>
  useQuery({
    ...controlTagCatalogQueryOptions,
    select: toLabeledControlTags,
    enabled: options?.enabled ?? true,
  });

export const useArchiveControlTag = () =>
  useMutation({
    mutationFn: async ({ controlTagId }: { controlTagId: string }) => {
      const updateTag = new UpdateControlTag(controlTagId, null, null, "archived", null);
      await clientSDK
        .updateControlTag(updateTag)
        .then(() => {
          toast.success("Control tag archived successfully");
        })
        .catch((err: Error) => {
          toast.error("An error occurred while archiving the control tag");
          throw new Error("An error occurred while archiving the control tag.", { cause: err });
        });
    },
    onSuccess: invalidateControlTagCatalogs,
  });

export const useRestoreControlTag = () =>
  useMutation({
    mutationFn: async ({ controlTagId }: { controlTagId: string }) => {
      const updateTag = new UpdateControlTag(controlTagId, null, null, "active", null);
      await clientSDK
        .updateControlTag(updateTag)
        .then(() => {
          toast.success("Control tag restored successfully");
        })
        .catch((err: Error) => {
          toast.error("An error occurred while restoring the control tag");
          throw new Error("An error occurred while restoring the control tag.", { cause: err });
        });
    },
    onSuccess: invalidateControlTagCatalogs,
  });

export type ControlTagIdsActionTypesType =
  | {
      ids: string[];
      action: "set" | "delete";
    }
  | undefined;

function applyControlTagActions<T extends { id: string }, U extends { id: string }>(
  currentTags: T[],
  allTags: U[],
  controlTagIdsActionTypes: ControlTagIdsActionTypesType[],
): Array<T | U> {
  let updatedTags: Array<T | U> = [...currentTags];

  for (const actionItem of controlTagIdsActionTypes) {
    if (!actionItem) continue;
    const { ids, action } = actionItem;

    if (action === "set") {
      const tagsToAdd = allTags.filter((t) => ids.includes(t.id) && !updatedTags.some((ut) => ut.id === t.id));
      updatedTags = [...updatedTags, ...tagsToAdd];
    } else if (action === "delete") {
      updatedTags = updatedTags.filter((t) => !ids.includes(t.id));
    }
  }

  return updatedTags;
}

export const usePatchModelControlTags = (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
  useMutation({
    mutationFn: async ({
      modelId,
      controlTagIdsActionTypes,
      reasonText,
    }: {
      modelId: string;
      controlTagIdsActionTypes: ControlTagIdsActionTypesType[];
      reasonText?: string;
    }) => {
      const list = controlTagIdsActionTypes.map((tagActionItem) =>
        clientSDK.patchModelControlTaggings(modelId, tagActionItem?.ids || [], tagActionItem?.action, reasonText),
      );
      await Promise.all(list).catch((err: Error) => {
        console.error(err);
        toast.error("An error occurred while updating the control tag(s) to model");
        throw new Error("An error occurred while updating control tag(s) to model.", { cause: err });
      });
      return { modelId };
    },
    onMutate: async ({ modelId, controlTagIdsActionTypes }) => {
      const resourceQueryKey = resourceQueryKeys.byId(modelId);
      await queryClient.cancelQueries({ queryKey: modelQueryKeys.itemFull(modelId) });
      await queryClient.cancelQueries({ queryKey: resourceQueryKey });

      const previousModelData = queryClient.getQueryData<{ modelTags?: LabeledControlTag[] }>(
        modelQueryKeys.itemFull(modelId),
      );
      const previousResourceData = queryClient.getQueryData<V3Models.ResourceDto>(resourceQueryKey);

      if (previousModelData) {
        const allTags = getLabeledControlTagsFromCache();
        const updatedTags = applyControlTagActions(
          previousModelData.modelTags || [],
          allTags,
          controlTagIdsActionTypes,
        );

        queryClient.setQueryData(modelQueryKeys.itemFull(modelId), {
          ...previousModelData,
          modelTags: updatedTags,
        });
      }

      if (previousResourceData) {
        const allTags = getLabeledControlTagsFromCache();
        const updatedTags = applyControlTagActions(
          previousResourceData.controlTags || [],
          allTags,
          controlTagIdsActionTypes,
        );

        queryClient.setQueryData(resourceQueryKey, { ...previousResourceData, controlTags: updatedTags });
      }

      return { previousModelData, previousResourceData, modelId };
    },
    onError: (error, _variables, context) => {
      if (context?.previousModelData) {
        queryClient.setQueryData(modelQueryKeys.itemFull(context.modelId), context.previousModelData);
      }
      if (context?.previousResourceData) {
        queryClient.setQueryData(resourceQueryKeys.byId(context.modelId), context.previousResourceData);
      }
      options?.onError?.(error);
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: modelQueryKeys.itemFull(variables.modelId) });
      queryClient.invalidateQueries({ queryKey: resourceQueryKeys.byId(variables.modelId) });
    },
    meta: {
      skipGlobalInvalidation: true,
    },
  });

export const usePatchArtifactControlTags = (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
  useMutation({
    mutationFn: async ({
      artifactId,
      controlTagIdsActionTypes,
      reasonText,
    }: {
      artifactId: string;
      controlTagIdsActionTypes: ControlTagIdsActionTypesType[];
      reasonText?: string;
    }) => {
      const list = controlTagIdsActionTypes.map((tagActionItem) =>
        clientSDK.patchArtifactControlTaggings(artifactId, tagActionItem?.ids || [], tagActionItem?.action, reasonText),
      );
      await Promise.all(list).catch((err: Error) => {
        console.error(err);
        toast.error("An error occurred while updating the control tag(s) to artifact");
        throw new Error("An error occurred while updating control tag(s) to artifact.", { cause: err });
      });
      return { artifactId };
    },
    onMutate: async ({ artifactId, controlTagIdsActionTypes }) => {
      const resourceQueryKey = resourceQueryKeys.byId(artifactId);
      await queryClient.cancelQueries({ queryKey: resourceQueryKey });
      const previousResourceData = queryClient.getQueryData<V3Models.ResourceDto>(resourceQueryKey);
      if (previousResourceData) {
        const allTags = getLabeledControlTagsFromCache();
        const updatedTags = applyControlTagActions(
          previousResourceData.controlTags || [],
          allTags,
          controlTagIdsActionTypes,
        );

        queryClient.setQueryData(resourceQueryKey, { ...previousResourceData, controlTags: updatedTags });
      }

      await queryClient.cancelQueries({ queryKey: artifactQueryKeys.item(artifactId) });
      const previousArtifactData = queryClient.getQueryData<Artifact>(artifactQueryKeys.item(artifactId));

      if (previousArtifactData) {
        const allTags = getLabeledControlTagsFromCache();
        const updatedTags = applyControlTagActions(
          previousArtifactData.controlTags || [],
          allTags,
          controlTagIdsActionTypes,
        );

        queryClient.setQueryData(artifactQueryKeys.item(artifactId), {
          ...previousArtifactData,
          controlTags: updatedTags as ControlTag[],
        });
      }

      return { previousResourceData, previousArtifactData, artifactId };
    },
    onError: (error, _variables, context) => {
      if (context?.previousArtifactData) {
        queryClient.setQueryData(artifactQueryKeys.item(context.artifactId), context.previousArtifactData);
      }
      if (context?.previousResourceData) {
        queryClient.setQueryData(resourceQueryKeys.byId(context.artifactId), context.previousResourceData);
      }
      options?.onError?.(error);
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: artifactQueryKeys.item(variables.artifactId) });
      queryClient.invalidateQueries({ queryKey: resourceQueryKeys.byId(variables.artifactId) });
    },
  });

export const usePatchUserControlTags = (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
  useMutation({
    mutationFn: async ({
      userId,
      controlTagIdsActionTypes,
      reasonText,
    }: {
      userId: string;
      controlTagIdsActionTypes: ControlTagIdsActionTypesType[];
      reasonText?: string;
    }) => {
      const list = controlTagIdsActionTypes.map((tagActionItem) =>
        clientSDK.patchUserControlTaggings(userId, tagActionItem?.ids || [], tagActionItem?.action, reasonText),
      );
      await Promise.all(list).catch((err: Error) => {
        console.error(err);
        toast.error("An error occurred while updating the control tag(s) to user");
        throw new Error("An error occurred while updating control tag(s) to user.", { cause: err });
      });
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
